import _ from 'lodash';

import { Attribute } from './attribute';
import { AttributeDefinitionValueListItem } from './attributeDefinitionValueListItem';
import { AttributeDefinition } from './attributeDefinition';
import { AttributeDefinitionCondition } from './attributeDefinitionCondition';
import { FlatAttribute, FlatComplexAttributeValue } from './flatAttribute';
import moment from 'moment';
import { AttributeUiType } from './attributeUiType';
import { FieldLabelProps } from 'src/shared-app-portal/components/field-label/field-label-props';

export class EditableAttribute extends Attribute implements FieldLabelProps {
  public _min: string;
  public _max: string;
  private _isVisible: boolean;
  public conditions: AttributeDefinitionCondition[];
  public complexAttributeValues: FlatComplexAttributeValue[];
  public decryptedValue: string;
  public showCurrentValue: boolean;

  public static readonly INVALID_DATE_VALUE: string = 'Invalid Date';

  constructor(attribute: Attribute, definition: AttributeDefinition) {
    super(
      attribute.id,
      attribute.name,
      definition.description,
      definition.adminDocumentation,
      attribute.value,
      attribute.type,
      definition.entityType,
      attribute.attributeDefinitionId,
      definition.required,
      definition.isReadOnly,
      definition.isHidden,
      attribute.featured,
      definition.allowsUserAddedValues,
      attribute.createdBy,
      attribute.createdOn,
      attribute.updatedBy,
      attribute.updatedOn,
      attribute.edited ? attribute.edited : false,
      attribute.otherValue
    );
    this.name = definition.name;
    this.description = definition.description;
    this.adminDocumentation = definition.adminDocumentation;
    this._min = definition.min;
    this._max = definition.max;
    this.valueList = [];
    this.attributeDefinitionValueListItem = attribute.attributeDefinitionValueListItem;
    this.conditions = definition.conditions;
    this._isVisible = true;
    this.subFieldList = definition.subFieldList;
    this.showCurrentValue = definition.showCurrentValue;
  }

  public get isVisible(): boolean {
    return this._isVisible;
  }

  public hide() {
    this._isVisible = false;
  }

  public show() {
    this._isVisible = true;
  }

  public get hasCondition(): boolean {
    return this.conditions && this.conditions.length > 0 && this.hasAnyConditionValue;
  }
  public get allowsMultipleValues(): boolean {
    return super.isCheckboxType() || super.isMultiSelectType();
  }

  private get hasAnyConditionValue(): boolean {
    let result = this.conditions.findIndex((x) => x.conditionValues.length > 0) != -1;
    return result;
  }
  public get needsRangeCheck(): boolean {
    return super.isNumberType() && this.hasRange;
  }

  public get maxSelections(): number | null {
    // Mirror the backend AttributeDefinition.MaxSelections: only a positive whole number is a
    // usable limit; unset, non-numeric, decimal, or <1 legacy values mean "no limit".
    if (!super.isCheckboxType()) {
      return null;
    }
    const parsed = Number(this._max);
    return Number.isInteger(parsed) && parsed >= 1 ? parsed : null;
  }

  public get exceedsMaxSelections(): boolean {
    return this.maxSelections != null && this.valueList.length > this.maxSelections;
  }

  // A checkbox field with a limit participates in validation even when not required, so the
  // datasource/form validation gates evaluate it (otherwise over-max saves slip past to a 400).
  public get needsMaxSelectionsCheck(): boolean {
    return this.maxSelections != null;
  }

  public get needsRangeCheckComplex(): boolean {
    return super.isComplexType() && this.hasRange && !this.isReadOnly;
  }

  public get needsDateRangeCheck(): boolean {
    return super.isDateRangeType();
  }

  public get needsDateCheck(): boolean {
    return super.isDateType();
  }

  public get needsOtherCheck(): boolean {
    if (this.isReadOnly) {
      return false;
    }
    return (
      (super.isDropDownType() && this.attributeDefinitionValueListItem?.isOther) ||
      (super.isRadioButtonGroupType() && this.attributeDefinitionValueListItem?.isOther) ||
      (super.isCheckboxType() && this.valueList.filter((item) => item.isOther).length > 0) ||
      (super.isMultiSelectType() && this.valueList.filter((item) => item.isOther).length > 0)
    );
  }

  public get isValid(): boolean {
    if (!this.isVisible || this.isReadOnly) {
      return true;
    }

    if (this.exceedsMaxSelections) {
      return false;
    }

    if (this.required && !this.hasValue()) {
      return false;
    }

    if (this.needsRangeCheck && this.hasValue()) {
      return this.isInRange;
    }

    if (this.needsOtherCheck && !this.otherValue) {
      return false;
    }

    if (this.needsDateRangeCheck) {
      return this.isInDateRange;
    }

    if (this.needsDateCheck) {
      return this.isValidDate;
    }

    if (this.needsRangeCheckComplex) {
      const inRange = this.complexInRange;
      const allValuesValid =
        this.complexAttributeValues.length === 0 ||
        this.complexAttributeValues.every((cv) => this.isComplexValueValid(cv));
      return inRange && allValuesValid;
    }

    if (this.isComplexType() && this.complexAttributeValues.length > 0) {
      return this.complexAttributeValues.every((cv) => this.isComplexValueValid(cv));
    }

    return true;
  }

  public get profileIsValid(): boolean {
    if (!this.isVisible || this.isReadOnly) {
      return true;
    }

    if (this.exceedsMaxSelections) {
      return false;
    }

    if (this.required && !this.hasValue()) {
      return false;
    }

    if (this.needsRangeCheck && this.hasValue()) {
      return this.isInRange;
    }

    if (this.needsOtherCheck && !this.otherValue) {
      return false;
    }

    if (this.needsDateRangeCheck) {
      return this.isInDateRange;
    }

    if (this.needsDateCheck) {
      return this.isValidDate;
    }

    return true;
  }

  public setOrAddValue(value: string | AttributeDefinitionValueListItem | null) {
    if (super.isDropDownType() || super.isRadioButtonGroupType()) {
      this.attributeDefinitionValueListItem = <AttributeDefinitionValueListItem>value;
      this.value = this.attributeDefinitionValueListItem?.itemValue;
    } else if (this.allowsMultipleValues) {
      let attributeDefinitionValueListItem = AttributeDefinitionValueListItem.parse(
        value as AttributeDefinitionValueListItem
      );
      this.valueList.push(attributeDefinitionValueListItem);
    } else {
      this.value = <string>value;
    }
  }

  public removeValue(value: string) {
    _.remove(this.valueList, { itemValue: value });
  }

  public hasValue(): boolean {
    if (this.isComplexType()) {
      if (!this.complexAttributeValues?.length) {
        return false;
      }
      if (this.isReadOnly) {
        return true; //if it's readonly and complexAttributeValues.length it "has a value" even if required subfields are not filled in.
      }
      let requiredSubFieldList = this.subFieldList
        .filter((x) => x.required == true)
        .map((y) => y.id);
      let complexAttributeItems = this.complexAttributeValues[0].items.map(
        (x) => x.subFieldDefinitionItemId
      );
      return requiredSubFieldList.every((item) => complexAttributeItems.includes(item));
    }
    return this.allowsMultipleValues ? !_.isEmpty(this.valueList) : super.hasValue();
  }

  public isComplexValueValid(complexValue: FlatComplexAttributeValue): boolean {
    let requiredSubFieldList = this.subFieldList.filter((x) => x.required == true).map((y) => y.id);
    let complexAttributeItems = complexValue.items.map((x) => x.subFieldDefinitionItemId);
    return requiredSubFieldList.every((item) => complexAttributeItems.includes(item));
  }

  public get complexInRange(): boolean {
    let minInRange =
      this._min && this.required ? this.complexAttributeValues.length >= Number(this._min) : true;
    let maxIRange = this._max ? this.complexAttributeValues.length <= Number(this._max) : true;
    return minInRange && maxIRange;
  }

  private get hasRange(): boolean {
    const hasMin = !isNaN(Number(this._min));
    const hasMax = !isNaN(Number(this._max));
    return hasMin || hasMax;
  }

  private get isInRange(): boolean {
    const isValueParseable = (limit: string): boolean => !_.isNull(limit) && !_.isUndefined(limit);
    const hasMin = isValueParseable(this._min) && !isNaN(Number(this._min));
    const hasMax = isValueParseable(this._max) && !isNaN(Number(this._max));
    const hasValue = isValueParseable(this.value) && !isNaN(Number(this.value));

    if (!hasValue) {
      return !this.required;
    }

    let value = Number(this.value);
    let min = Number(this._min);
    let max = Number(this._max);

    if (hasMin && hasMax) {
      return value >= min && max >= value;
    } else if (hasMin) {
      return value >= min;
    } else if (hasMax) {
      return max >= value;
    } else {
      return true;
    }
  }

  private get isValidDate(): boolean {
    if (this.value === EditableAttribute.INVALID_DATE_VALUE) {
      return false;
    }
    return true;
  }

  private get isInDateRange(): boolean {
    let startDate;
    let endDate;
    let present: boolean = false;
    if (this.value) {
      let valueArray = this.value.replace(/ /g, '').split('|');
      if (valueArray.length > 0) {
        startDate = new Date(valueArray[0]);
        if (isNaN(startDate)) {
          return false;
        }
        if (valueArray.length == 2) {
          if (valueArray[1].localeCompare('Present', 'en', { sensitivity: 'base' }) == 0) {
            present = true;
          } else {
            endDate = new Date(valueArray[1]);
            if (isNaN(endDate)) {
              return false;
            }
          }
        }
        if (startDate && endDate && moment(startDate).isAfter(moment(endDate))) {
          return false;
        } else if (!startDate && (present || endDate)) {
          return false;
        } else if (startDate && !(present || endDate)) {
          return false;
        }
      }
    }
    return true;
  }

  public values(): string[] {
    let values: string[] = [];
    if (super.isDropDownType() || super.isRadioButtonGroupType() || super.isSkillType()) {
      values.push(this.attributeDefinitionValueListItem?.id);
    } else if (this.allowsMultipleValues) {
      values = this.valueList.map((x) => x.id);
    } else {
      values.push(this.value);
    }
    return values;
  }

  static fromAttributeDefinition(attributeDefinition: AttributeDefinition): EditableAttribute {
    return new EditableAttribute(
      Attribute.parse({
        name: attributeDefinition.name,
        description: attributeDefinition.description,
        type: attributeDefinition.uiType,
        entityType: attributeDefinition.entityType,
        attributeDefinitionId: attributeDefinition.id,
        required: attributeDefinition.required,
        allowsUserAddedValues: attributeDefinition.allowsUserAddedValues,
        valueList: attributeDefinition.valueList
      }),
      attributeDefinition
    );
  }

  public static fromAttributeDefinitionAndAttribute = (
    attributeDefinition: AttributeDefinition,
    filteredAndCloned: Attribute[]
  ): EditableAttribute => {
    if (attributeDefinition.uiType === AttributeUiType.Complex) {
      const complexValues = filteredAndCloned
        .filter((attr) => attr.attributeDefinitionId == attributeDefinition.id)
        .map((attr) => attr.complexAttributeSubFieldValue);

      let attribute = EditableAttribute.fromAttributeDefinition(attributeDefinition);
      attribute.complexAttributeValues = FlatAttribute.flatComplexValues(complexValues);
      return attribute;
    }
    if (attributeDefinition.allowsMultipleValues) {
      const allValues = filteredAndCloned.filter(
        (attr) => attr.attributeDefinitionId == attributeDefinition.id
      );

      const valueList = allValues
        .map((a) => a.attributeDefinitionValueListItem)
        .sort((a, b) => a.rank - b.rank);
      const attribute = EditableAttribute.fromAttributeDefinition(attributeDefinition);
      attribute.valueList = valueList;
      attribute.otherValue = allValues.find(
        (attr) => attr.attributeDefinitionValueListItem.isOther
      )?.otherValue;
      return attribute;
    }
    const value = filteredAndCloned.find(
      (attr) => attr.attributeDefinitionId == attributeDefinition.id
    );
    if (value) {
      return new EditableAttribute(value, attributeDefinition);
    }

    //attribute has no value
    return EditableAttribute.fromAttributeDefinition(attributeDefinition);
  };
}
